import { Button, FormField, Input } from "@/ui";
/**
 * EmailCaptureModal Component
 *
 * Exit-intent popup to capture emails with discount incentive.
 * Triggers on:
 * - Exit intent (mouse leaves viewport top) - Desktop only
 * - After 30 seconds on page without interaction
 *
 * Features:
 * - Respects localStorage to not show again for 7 days
 * - Does not show if user is logged in
 * - Does not show on mobile (exit intent doesn't work)
 * - Full analytics tracking
 *
 * TODO: Refactor to use Modal component from UI layer per docs/ui-contract.md
 * This component has marketing-specific modal styling that should use
 * the existing Modal/Dialog components from @/ui with appropriate variants.
 *
 * @file EmailCaptureModal.tsx
 * @version 1.0.0 - November 2025
 */

/* eslint-disable @innolek/no-tailwind-outside-ui */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AnimatePresence, motion } from 'framer-motion';
import { Gift, Sparkles, X } from 'lucide-react';

import { ENGAGEMENT_EVENTS } from '@/shared/analytics';
import { useAnalytics, useIsMobile } from '@/shared/hooks';
import { marketingApiClient } from '../api/marketing.api';

// =============================================================================
// CONSTANTS
// =============================================================================

const STORAGE_KEY = 'tangi_email_capture_shown';
const STORAGE_DISMISSED_KEY = 'tangi_email_capture_dismissed';
const DAYS_TO_HIDE = 7;
const IDLE_TIMEOUT_MS = 30000; // 30 seconds
// Note: Discount code is now fetched from backend API (single source of truth)

// Static animation variants - extracted for performance
const BACKDROP_INITIAL = { opacity: 0 } as const;
const BACKDROP_ANIMATE = { opacity: 1 } as const;
const BACKDROP_EXIT = { opacity: 0 } as const;
const BACKDROP_TRANSITION = { duration: 0.2 } as const;

const MODAL_INITIAL = { scale: 0.95, y: 20 } as const;
const MODAL_ANIMATE = { scale: 1, y: 0 } as const;
const MODAL_EXIT = { scale: 0.95, y: 20 } as const;
const MODAL_TRANSITION = { type: 'spring', damping: 25, stiffness: 300 } as const;

const SUCCESS_INITIAL = { opacity: 0, y: 10 } as const;
const SUCCESS_ANIMATE = { opacity: 1, y: 0 } as const;

// =============================================================================
// TYPES
// =============================================================================

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailFormData = z.infer<typeof emailSchema>;

interface EmailCaptureModalProps {
  /** Override for testing - force show modal */
  forceShow?: boolean;
  /** Callback when email is successfully captured */
  onSuccess?: (email: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({
  forceShow = false,
  onSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState<string | null>(null);

  const { trackEngagement } = useAnalytics();
  const isMobile = useIsMobile();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  // Check if modal should be shown
  const shouldShowModal = useCallback((): boolean => {
    if (forceShow) return true;

    // Don't show on mobile (exit intent doesn't work)
    if (isMobile) return false;

    // Check if already shown recently
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown) {
      const lastShownDate = new Date(lastShown);
      const daysSinceShown = (Date.now() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceShown < DAYS_TO_HIDE) return false;
    }

    // Check if dismissed
    const dismissed = localStorage.getItem(STORAGE_DISMISSED_KEY);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < DAYS_TO_HIDE) return false;
    }

    return true;
  }, [forceShow, isMobile]);

  // Show modal and track
  const showModal = useCallback(() => {
    if (hasShownRef.current || !shouldShowModal()) return;
    hasShownRef.current = true;

    setIsOpen(true);
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());

    trackEngagement(ENGAGEMENT_EVENTS.EMAIL_MODAL_SHOWN, {});
  }, [shouldShowModal, trackEngagement]);

  // Force show modal immediately when forceShow prop is true
  useEffect(() => {
    if (forceShow) {
      showModal();
    }
  }, [forceShow, showModal]);

  // Handle exit intent (mouse leaves viewport)
  useEffect(() => {
    if (forceShow) return; // Skip if force showing
    if (isMobile || !shouldShowModal()) return;

    const handleMouseLeave = (e: MouseEvent): void => {
      // Only trigger when mouse leaves from the top
      if (e.clientY <= 0) {
        showModal();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [forceShow, isMobile, shouldShowModal, showModal]);

  // Handle idle timeout
  useEffect(() => {
    if (forceShow) return; // Skip if force showing
    if (isMobile || !shouldShowModal()) return;

    const resetIdleTimer = (): void => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        showModal();
      }, IDLE_TIMEOUT_MS);
    };

    // Reset timer on user activity
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      document.addEventListener(event, resetIdleTimer, { passive: true });
    });

    // Start initial timer
    resetIdleTimer();

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [forceShow, isMobile, shouldShowModal, showModal]);

  // Handle close
  const handleClose = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_DISMISSED_KEY, new Date().toISOString());

    trackEngagement(ENGAGEMENT_EVENTS.EMAIL_MODAL_DISMISSED, {});
  }, [trackEngagement]);

  // Handle form submission
  const onSubmit = async (data: EmailFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Determine source based on how modal was triggered
      const apiSource = hasShownRef.current ? 'exit_intent' : 'idle_timeout';

      // Call email capture API
      const response = await marketingApiClient.captureEmail({
        email: data.email,
        source: apiSource,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to capture email');
      }

      // Store discount code from response (single source of truth)
      setDiscountCode(response.discount_code || null);
      setIsSuccess(true);
      reset();

      // Map API source to analytics source (analytics doesn't support 'idle_timeout')
      const analyticsSource: 'exit_intent' | 'footer' | 'checkout' | 'popup' | 'other' =
        apiSource === 'exit_intent' ? 'exit_intent' : 'popup';

      trackEngagement(ENGAGEMENT_EVENTS.EMAIL_CAPTURED, {
        source: analyticsSource,
        marketing_opt_in: true,
      });

      onSuccess?.(data.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={BACKDROP_INITIAL}
            animate={BACKDROP_ANIMATE}
            exit={BACKDROP_EXIT}
            transition={BACKDROP_TRANSITION}
            className="fixed inset-0 z-overlay bg-background/80 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Modal Container - Flexbox centering for reliable responsive positioning */}
          <motion.div
            initial={BACKDROP_INITIAL}
            animate={BACKDROP_ANIMATE}
            exit={BACKDROP_EXIT}
            transition={BACKDROP_TRANSITION}
            className="fixed inset-0 z-modal flex items-center justify-center p-4 sm:p-6 overflow-y-auto overscroll-contain"
            onClick={handleClose}
          >
            {/* Modal Card */}
            <motion.div
              initial={MODAL_INITIAL}
              animate={MODAL_ANIMATE}
              exit={MODAL_EXIT}
              transition={MODAL_TRANSITION}
              className="relative w-full max-w-md my-auto overflow-hidden rounded-2xl bg-card shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-capture-title"
              aria-describedby="email-capture-description"
            >
              {/* Decorative gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary-color)/0.1)] via-card to-[hsl(var(--accent-color)/0.1)]" />

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute right-3 top-3 z-10 rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted active:bg-muted hover:text-foreground active:text-foreground min-h-11 min-w-11 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content */}
              <div className="relative p-5 sm:p-8">
                {!isSuccess ? (
                  <>
                    {/* Icon */}
                    <div className="mx-auto mb-4 sm:mb-6 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-gradient-to-br from-[hsl(var(--primary-color))] to-[hsl(var(--accent-color))] shadow-lg">
                      <Gift className="h-7 w-7 sm:h-8 sm:w-8 text-primary-foreground" />
                    </div>

                    {/* Title */}
                    <h2
                      id="email-capture-title"
                      className="mb-2 text-center text-body-xl sm:text-heading-2 font-bold text-foreground"
                    >
                      Wait! Get 10% Off
                    </h2>

                    {/* Description */}
                    <p
                      id="email-capture-description"
                      className="mb-4 sm:mb-6 text-center text-body-sm-responsive sm:text-body-responsive text-muted-foreground"
                    >
                      Join 1,000+ creators who&apos;ve brought their ideas to life.
                      Get your exclusive discount code!
                    </p>

                    {/* Form */}
                    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-3 sm:space-y-4">
                      <FormField
                        label="Email Address"
                        id="email"
                        required
                        {...(errors.email ? { error: errors.email.message } : {})}
                        visuallyHiddenLabel
                      >
                        <Input
                          {...register('email')}
                          type="email"
                          placeholder="Enter your email"
                          inputSize="lg"
                          error={!!errors.email}
                        />
                      </FormField>

                      {error && (
                        <p className="text-body-sm-responsive text-destructive" role="alert">
                          {error}
                        </p>
                      )}

                      <Button
                        type="submit"
                        variant="tangi"
                        size="xl"
                        fullWidth
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            <Sparkles className="h-5 w-5" />
                            Get My Discount
                          </>
                        )}
                      </Button>
                    </form>

                    {/* Privacy note */}
                    <p className="mt-3 sm:mt-4 text-center text-caption text-muted-foreground">
                      We respect your privacy. Unsubscribe anytime.
                    </p>
                  </>
                ) : (
                  /* Success State */
                  <motion.div
                    initial={SUCCESS_INITIAL}
                    animate={SUCCESS_ANIMATE}
                    className="py-2 sm:py-4 text-center"
                  >
                    {/* Success icon */}
                    <div className="mx-auto mb-4 sm:mb-6 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg">
                      <Sparkles className="h-7 w-7 sm:h-8 sm:w-8 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-body-xl sm:text-heading-2 font-bold text-foreground">
                      You&apos;re In! 🎉
                    </h3>

                    <p className="mb-4 sm:mb-6 text-body-sm-responsive sm:text-body-responsive text-muted-foreground">
                      Your discount code is:
                    </p>

                    {/* Discount code */}
                    {discountCode && (
                      <div className="mx-auto mb-4 sm:mb-6 w-fit rounded-lg border-2 border-dashed border-primary bg-primary/5 px-4 sm:px-6 py-2 sm:py-3">
                        <span className="font-mono text-body-lg sm:text-heading-2 font-bold tracking-wider text-primary">
                          {discountCode}
                        </span>
                      </div>
                    )}

                    <p className="mb-4 sm:mb-6 text-body-sm-responsive text-muted-foreground">
                      Use this code at checkout for 10% off your first order!
                    </p>

                    <Button
                      onClick={handleClose}
                      variant="tangi"
                      size="lg"
                      fullWidth
                    >
                      Start Creating
                    </Button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmailCaptureModal;
